import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  Card,
  Checkbox,
  EmptyState,
  FormField,
  Spinner,
  TextInput,
  Textarea,
  useToast,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import { adminFetch } from '../auth/apiClient';
import { ApiError, type ActiveCurrencyRates, type CurrencyRate } from '../auth/types';
import { useAuth } from '../auth/useAuth';
import { translateApiError } from '../auth/errors';
import { PageHeader } from '../components/PageHeader';

interface FormValues {
  readonly rate: string;
  readonly reason: string;
  readonly confirmLargeDelta: boolean;
}

function findUsdLbpRate(rates: ActiveCurrencyRates | null): CurrencyRate | null {
  if (!rates) return null;
  return rates.quotes.find((q) => q.baseCurrency === 'USD' && q.quoteCurrency === 'LBP') ?? null;
}

export function FinancialPage() {
  const { t, locale } = useI18n();
  const toast = useToast();
  const { adminUser } = useAuth();
  const canWrite = adminUser?.permissions.includes('currency:write') ?? false;

  const [rates, setRates] = useState<ActiveCurrencyRates | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [formBanner, setFormBanner] = useState<{
    tone: 'success' | 'danger' | 'warning';
    title?: string | undefined;
    body?: string | undefined;
  } | null>(null);

  const schema = z.object({
    rate: z
      .string()
      .min(1, t('admin:validation.required'))
      .refine((v) => {
        const n = Number(v);
        return Number.isFinite(n) && n > 0;
      }, t('admin:errors.rateOutOfBounds')),
    reason: z.string().min(1, t('admin:validation.required')).max(280),
    confirmLargeDelta: z.boolean(),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { rate: '', reason: '', confirmLargeDelta: false },
  });

  const fetchRates = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const body = await adminFetch<{ data: ActiveCurrencyRates }>('/currency/rates');
      setRates(body.data);
    } catch (err) {
      setFetchError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // Fetch-on-mount; setState happens inside fetchRates which is the
    // canonical async-load pattern.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchRates();
  }, [fetchRates]);

  const activeRate = findUsdLbpRate(rates);

  const onSubmit = form.handleSubmit(async (values) => {
    setFormBanner(null);
    try {
      await adminFetch('/admin/currency/rates', {
        method: 'PUT',
        body: {
          base: 'USD',
          quote: 'LBP',
          rate: Number(values.rate),
          reason: values.reason,
          confirmLargeDelta: values.confirmLargeDelta,
        },
      });
      toast.push({
        tone: 'success',
        title: t('admin:financial.form.successTitle'),
        description: t('admin:financial.form.successBody'),
      });
      form.reset({ rate: '', reason: '', confirmLargeDelta: false });
      await fetchRates();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'LARGE_DELTA_REJECTED') {
          const prev = err.body?.error?.previousRate;
          const next = err.body?.error?.newRate;
          // FIX 5.22 (§13.11-3) — the API sends `deltaPct` as a fraction
          // (|new - prev| / prev), so a 25 % jump is 0.25.
          const delta = err.body?.error?.deltaPct;
          setFormBanner({
            tone: 'warning',
            title: t('admin:errors.largeDelta'),
            body:
              prev !== undefined && next !== undefined && delta !== undefined
                ? `${formatNumber(prev, locale)} -> ${formatNumber(next, locale)} (${(delta * 100).toFixed(1)}%)`
                : undefined,
          });
          return;
        }
        if (err.code === 'RATE_OUT_OF_BOUNDS') {
          const min = err.body?.error?.min;
          const max = err.body?.error?.max;
          setFormBanner({
            tone: 'danger',
            title: t('admin:errors.rateOutOfBounds'),
            body:
              min !== undefined && max !== undefined
                ? `${formatNumber(min, locale)} – ${formatNumber(max, locale)}`
                : undefined,
          });
          return;
        }
      }
      setFormBanner({ tone: 'danger', title: translateApiError(err, t) });
    }
  });

  return (
    <div className="space-y-6">
      <PageHeader title={t('admin:nav.financial')} />

      <Card>
        <div className="space-y-3">
          <div>
            <h2 className="text-h3 font-bold text-ink">{t('admin:financial.currentRate.title')}</h2>
            <p className="text-[12.5px] text-ink-2 mt-1">
              {t('admin:financial.currentRate.subtitle')}
            </p>
          </div>
          {fetchError ? (
            <Banner tone="danger">{fetchError}</Banner>
          ) : loading ? (
            <div className="flex items-center justify-center py-6">
              <Spinner />
            </div>
          ) : activeRate ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-ink-3">
                  {t('admin:financial.currentRate.rate')}
                </div>
                <div className="text-[24px] font-extrabold text-ink tabular-nums mt-1">
                  {formatNumber(activeRate.rate, locale)}{' '}
                  <span className="text-[14px] font-bold text-ink-2">LBP</span>
                </div>
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-ink-3">
                  {t('admin:financial.currentRate.asOf')}
                </div>
                <div className="text-[13px] text-ink mt-1 font-mono">
                  {formatDate(activeRate.effectiveAt, locale)}
                </div>
              </div>
              <div>
                <div className="text-[11px] font-mono uppercase tracking-wider text-ink-3">
                  {t('admin:financial.currentRate.source')}
                </div>
                <div className="text-[13px] text-ink mt-1 font-mono">{activeRate.source}</div>
              </div>
            </div>
          ) : (
            <Banner tone="warning">{t('admin:financial.currentRate.noRate')}</Banner>
          )}
        </div>
      </Card>

      {canWrite ? (
        <Card>
          <div className="space-y-4">
            <div>
              <h2 className="text-h3 font-bold text-ink">{t('admin:financial.form.title')}</h2>
              <p className="text-[12.5px] text-ink-2 mt-1">{t('admin:financial.form.subtitle')}</p>
            </div>
            {formBanner ? (
              <Banner tone={formBanner.tone} title={formBanner.title}>
                {formBanner.body}
              </Banner>
            ) : null}
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <FormField
                label={t('admin:financial.form.rateLabel')}
                error={form.formState.errors.rate?.message}
                required
              >
                <TextInput
                  inputMode="decimal"
                  placeholder={t('admin:financial.form.ratePlaceholder')}
                  {...form.register('rate')}
                />
              </FormField>
              <FormField
                label={t('admin:financial.form.reasonLabel')}
                error={form.formState.errors.reason?.message}
                required
              >
                <Textarea
                  rows={3}
                  maxLength={280}
                  showCounter
                  placeholder={t('admin:financial.form.reasonPlaceholder')}
                  {...form.register('reason')}
                />
              </FormField>
              <Checkbox
                {...form.register('confirmLargeDelta')}
                label={t('admin:financial.form.confirmLargeDelta')}
              />
              <Button type="submit" variant="primary" loading={form.formState.isSubmitting}>
                {form.formState.isSubmitting
                  ? t('admin:financial.form.submitting')
                  : t('admin:financial.form.submit')}
              </Button>
            </form>
          </div>
        </Card>
      ) : (
        <Card>
          <p className="text-[12.5px] text-ink-2">{t('admin:financial.form.noPermission')}</p>
        </Card>
      )}

      <EmptyState
        icon={<UIIcon name="wallet" size={36} color="currentColor" />}
        title={t('admin:empty.financial.title')}
        description={t('admin:empty.financial.body')}
      />
    </div>
  );
}

function formatNumber(value: number, locale: string): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 4 }).format(value);
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}
